import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  useRouter as useRouterTanStack,
  useNavigate as useNavigateTanStack,
  type RegisteredRouter,
} from '@tanstack/react-router'
import {PREVENT_AUTOFOCUS_KEY, type NavigateOptionExtensions} from '../shared'
import {useCallback} from 'react'
import {isExternalToApp as isExternalToAppRouter} from './is-external-to-app'

/** Mirror react-router types */
export type URLSearchParamsInit = string | URLSearchParams | Array<[string, string]> | Record<string, string | string[]>
export type To = string | Partial<{pathname: string; search: string; hash: string}>
type ToObject = Exclude<To, string>
interface NavigateOptions {
  replace?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state?: any
  preventScrollReset?: boolean
  relative?: 'route' | 'path'
  flushSync?: boolean
  viewTransition?: boolean
}
export type NavigateOptionsWithPreventAutofocus = NavigateOptions & NavigateOptionExtensions

export {PREVENT_AUTOFOCUS_KEY} from '../shared'

/**
 * Translates React Router navigation calls to TanStack Router format.
 *
 * Forwarded compatible options:
 * - replace: replace history instead of push.
 * - state: stored in location state.
 * - viewTransition: document view transitions.
 * - reloadDocument: forwarded & force-set to true for routes detected as external to this app.
 * Mapped options:
 * - preventScrollReset: mapped to TanStack `resetScroll` (inverted value).
 * - relative: mapped to TanStack `unsafeRelative` when set to 'path'.
 * - preventAutofocus: mapped to a location state flag used by focus management.
 * Ignored options:
 * - preventTurbo: TanStack routing is not integrated with Turbo.
 * - flushSync: accepted for parity but currently a noop in this adapter.
 */
export const useNavigate = () => {
  const navigate = useNavigateTanStack()
  const router = useRouterTanStack()

  const isExternalToApp = useCallback(
    (path: string): boolean => (router ? isExternalToAppRouter(router, path) : true),
    [router],
  )

  return (to: To, options?: NavigateOptionsWithPreventAutofocus) => {
    const parsePathEnabled = isFeatureEnabled('tsr_use_navigate_parse_path')
    // TanStack Router reads a string `to` as a pathname only. A query or hash would stay in the pathname and
    // retained search params (like `_features`) would be added after it, so split them out like an object `to`.
    // A query- or hash-only string has no pathname, so it stays on the current page.
    const target = parsePathEnabled && typeof to === 'string' ? parsePath(to) : to

    // Determine the target path for route checking
    let targetPath: string | undefined

    if (typeof target === 'string') {
      targetPath = target
    } else if (target && typeof target === 'object' && target.pathname) {
      targetPath = target.pathname
    }

    const {
      preventScrollReset,
      relative,
      flushSync: _flushSync,
      preventAutofocus,
      preventTurbo: _preventTurbo,
      state,
      ...compatibleOptions
    } = options ?? {}

    const finalOptions: Record<string, unknown> = {
      ...compatibleOptions,
    }

    if (state !== undefined) {
      finalOptions.state = state
    }

    if (preventScrollReset !== undefined) {
      finalOptions.resetScroll = !preventScrollReset
    }

    if (relative === 'path') {
      finalOptions.unsafeRelative = 'path'
    }

    if (preventAutofocus) {
      finalOptions.state = {
        [PREVENT_AUTOFOCUS_KEY]: true,
        ...(state ?? {}),
      }
    }

    const isExternal = targetPath && isExternalToApp(targetPath)
    if (isExternal) {
      // Use the whole string so a full page load keeps its query and hash
      finalOptions.href = typeof to === 'string' ? to : targetPath
      finalOptions.to = undefined
      // Force a full-page navigation so TanStack Router doesn't attempt a
      // client-side route transition for paths outside the app's route tree.
      finalOptions.reloadDocument = true
    }

    // Handle string paths (e.g., navigate('/repos'))
    if (typeof target === 'string') {
      navigate({to: target, ...finalOptions})
      return
    }

    // Handle object paths with pathname and search
    if (target && typeof target === 'object') {
      navigate({...toTanStackNavigation(target, router, parsePathEnabled), ...finalOptions})
      return
    }

    // Fallback: pass through as-is
    navigate({to: target, ...finalOptions})
  }
}

/** Splits a path like `/repos?q=foo#top` into its parts, like React Router's `parsePath`. */
function parsePath(path: string): ToObject {
  const parsedPath: ToObject = {}
  let remainingPath = path

  const hashIndex = remainingPath.indexOf('#')
  if (hashIndex >= 0) {
    parsedPath.hash = remainingPath.slice(hashIndex)
    remainingPath = remainingPath.slice(0, hashIndex)
  }

  const searchIndex = remainingPath.indexOf('?')
  if (searchIndex >= 0) {
    parsedPath.search = remainingPath.slice(searchIndex)
    remainingPath = remainingPath.slice(0, searchIndex)
  }

  if (remainingPath) {
    parsedPath.pathname = remainingPath
  }

  return parsedPath
}

/**
 * React Router format: {pathname: '/repos', search: 'q=foo', hash: '#top'}
 * TanStack Router format: {to: '/repos', search: {q: 'foo'}, hash: 'top'}
 */
function toTanStackNavigation(
  {pathname, search, hash, ...rest}: ToObject,
  router: RegisteredRouter,
  stripHashPrefix: boolean,
) {
  // Build TanStack Router navigation object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tsrNavigation: Record<string, any> = {
    ...rest,
  }

  // Set the 'to' path
  if (pathname) {
    tsrNavigation.to = pathname
  }

  // Convert search string to object if needed
  // An empty string may be used to clear search params
  if (search !== undefined) {
    if (typeof search === 'string') {
      tsrNavigation.search = router.options.parseSearch(search)
    } else {
      tsrNavigation.search = search
    }
  }

  // Add hash if present. TanStack Router adds the `#` itself, so a leading `#` here would end up as `##`.
  const tanStackHash = stripHashPrefix && hash?.startsWith('#') ? hash.slice(1) : hash
  if (tanStackHash) {
    tsrNavigation.hash = tanStackHash
  }

  return tsrNavigation
}
